import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { CreditCard, ExternalLink, Gem, LayoutDashboard, LogOut, Palette, Package, Settings as Cog } from "lucide-react";
import { api, ADMIN_TOKEN } from "@/lib/api";
import { Emblem } from "@/components/Logo";

const NAV = [
  { to: "/admin", label: "Dashboard", Icon: LayoutDashboard, end: true },
  { to: "/admin/products", label: "Products", Icon: Gem },
  { to: "/admin/orders", label: "Orders", Icon: Package },
  { to: "/admin/payments", label: "Payments", Icon: CreditCard },
  { to: "/admin/design", label: "Website Design", Icon: Palette },
  { to: "/admin/settings", label: "Settings", Icon: Cog },
];

export default function AdminLayout() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => { localStorage.removeItem(ADMIN_TOKEN); nav("/admin/login", { replace: true }); });
  }, [nav]);
  const logout = async () => { await api.post("/auth/logout").catch(() => {}); localStorage.removeItem(ADMIN_TOKEN); nav("/admin/login"); };
  if (!user) return <div className="min-h-screen grid place-items-center bg-[#F3EEE8]"><Emblem className="w-14 h-12 animate-pulse text-plum" gem="#D2B27A" /></div>;

  const link = ({ isActive }) => `flex items-center gap-3 rounded-[14px] px-4 h-12 text-[15px] font-bold whitespace-nowrap transition-colors ${isActive ? "bg-pearl text-plum" : "text-pearl/80 hover:bg-pearl/10"}`;
  return (
    <div className="min-h-screen bg-[#F3EEE8] lg:flex" data-testid="admin-layout">
      <aside className="bg-plum text-pearl lg:w-64 lg:h-screen lg:sticky lg:top-0 flex lg:flex-col">
        <div className="hidden lg:flex items-center gap-2 px-6 h-20">
          <Emblem className="w-8 h-8" color="#F8F4EF" gem="var(--rh-accent)" />
          <span className="font-display text-[18px] tracking-[0.26em] font-semibold">RATNAHARA</span>
        </div>
        <nav className="flex lg:flex-col gap-1 p-2 lg:p-4 overflow-x-auto no-scrollbar flex-1">
          {NAV.map(({ to, label, Icon, end }) => (
            <NavLink key={to} to={to} end={end} className={link} data-testid={`admin-nav-${label.replace(/\s/g, "-").toLowerCase()}`}>
              <Icon className="w-[18px] h-[18px]" />{label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:block p-4 space-y-1 border-t border-pearl/10">
          <Link to="/" target="_blank" className="flex items-center gap-3 px-4 h-11 rounded-[14px] text-[14px] font-semibold text-pearl/75 hover:bg-pearl/10"><ExternalLink className="w-4 h-4" />View store</Link>
          <button onClick={logout} className="w-full flex items-center gap-3 px-4 h-11 rounded-[14px] text-[14px] font-semibold text-pearl/75 hover:bg-pearl/10" data-testid="admin-logout"><LogOut className="w-4 h-4" />Sign out</button>
          <p className="px-4 pt-2 text-[12px] text-pearl/50 truncate">{user.email}</p>
        </div>
      </aside>
      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-10 max-w-[1280px]">
        <div className="lg:hidden flex justify-end gap-4 mb-4 text-[13px] font-bold">
          <Link to="/" target="_blank">View store</Link><button onClick={logout}>Sign out</button>
        </div>
        <Outlet />
      </main>
    </div>
  );
}
